import React, { useState, useEffect, useMemo } from 'react';
import { Alert, Tabs } from '@opendate-app/setlist';
import ReportingPageLayout from './ReportingPageLayout';
import KpiCardGrid from './KpiCardGrid';
import SalesCard from './SalesCard';
import { buildSalesFilterGroups, withRoomFilterGroup } from '../constants/salesFiltersConfig';
import { SalesCardSkeleton } from './Skeletons';
import useReportingPage from '../hooks/useReportingPage';
import { currencyFormatter, compactCurrency, getSalesChartGranularity } from '../utils/reportingUtils';

const axios = require('axios').default;

const DATE_MODE_TABS = [
  { value: 'order_date', label: 'Order Date' },
  { value: 'event_date', label: 'Event Date' },
];

const buildApiParams = (activeFilters, dateMode, dateRange, granularity) => {
  const params = {};

  Object.entries(activeFilters).forEach(([key, value]) => {
    if (key === 'venue_ownership_id' && Array.isArray(value) && value.length > 0) {
      params.venue_ownership_id = value;
    }
    if (key === 'calendar_event_id' && Array.isArray(value) && value.length > 0) {
      params.calendar_event_id = value;
    }
    if (key === 'room_ids' && Array.isArray(value) && value.length > 0) {
      params.room_ids = value;
    }
    if (key === 'payment_type' && Array.isArray(value) && value.length > 0) {
      params.payment_type = value;
    }
    if (key === 'sources' && Array.isArray(value) && value.length > 0) {
      params.sources = value;
    }
  });

  if (dateRange && dateRange.start) params.start_date = dateRange.start;
  if (dateRange && dateRange.end) params.end_date = dateRange.end;

  if (dateMode === 'event_date') {
    params.date_mode = 'event_date';
  }

  params.granularity = granularity;

  return params;
};

function ReportingSalesPage({ team, csrfToken, venueOwnerships, eventOptions, roomOptions }) {
  const {
    isMobile, sidebarOpen, setSidebarOpen,
    dateRange, setDateRange,
    filterSidebarOpen, setFilterSidebarOpen,
    activeFilters, comparison,
    handleFilterChange, handleRemoveFilter, handleClearAllFilters,
  } = useReportingPage('this_month');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [priorData, setPriorData] = useState(null);
  const [dateMode, setDateMode] = useState('order_date');

  const granularity = getSalesChartGranularity(dateRange);
  const chartType = granularity === 'day' ? 'line' : 'bar';

  const filterGroups = useMemo(
    () => withRoomFilterGroup(buildSalesFilterGroups(venueOwnerships, eventOptions, [], { includeSources: true }), roomOptions),
    [venueOwnerships, eventOptions, roomOptions]
  );

  useEffect(() => {
    if (!team || !team.id) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (csrfToken) {
          axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
        }

        const params = buildApiParams(activeFilters, dateMode, dateRange, granularity);

        const [currentRes, priorRes] = await Promise.all([
          axios.get(`/teams/${team.id}/reporting/dashboard/sales/data.json`, { params: { ...params, sections: ['venue_series'] } }),
          comparison.start ? axios.get(`/teams/${team.id}/reporting/dashboard/sales/data.json`, {
            params: { ...buildApiParams(activeFilters, dateMode, { start: comparison.start, end: comparison.end }, granularity), sections: 'metrics' },
          }) : Promise.resolve(null),
        ]);

        setData(currentRes.data);
        setPriorData(priorRes ? priorRes.data : null);
      } catch (e) {
        setError('Failed to load sales data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [team?.id, csrfToken, activeFilters, dateMode, dateRange]);

  const safeData = data || {};
  const tickets = safeData.tickets || {};
  const addOns = safeData.add_ons || {};
  const donations = safeData.donations || {};
  const memberships = safeData.memberships || {};
  const packs = safeData.packs || {};
  const cash = safeData.cash || {};
  const venueSeries = safeData.venue_series || null;
  const venueNames = venueSeries ? venueSeries.names : [];

  const safePrior = priorData || {};
  const priorTickets = safePrior.tickets || {};
  const priorAddOns = safePrior.add_ons || {};
  const priorDonations = safePrior.donations || {};
  const priorMemberships = safePrior.memberships || {};
  const priorPacks = safePrior.packs || {};
  const priorCash = safePrior.cash || {};
  const priorSeries = (section) => (safePrior.granularity === safeData.granularity && section.over_time) || [];
  const cmpLabel = comparison.label;

  const kpiCards = [
    {
      top: { label: 'Ticket Revenue', value: compactCurrency(tickets.revenue || 0), current: tickets.revenue || 0, prior: priorTickets.revenue || 0 },
      bottom: { label: 'Tickets Sold', value: (tickets.count || 0).toLocaleString(), current: tickets.count || 0, prior: priorTickets.count || 0 },
    },
    {
      top: { label: 'Add-On Revenue', value: compactCurrency(addOns.revenue || 0), current: addOns.revenue || 0, prior: priorAddOns.revenue || 0 },
      bottom: { label: 'Add-Ons Sold', value: (addOns.count || 0).toLocaleString(), current: addOns.count || 0, prior: priorAddOns.count || 0 },
    },
    {
      top: { label: 'Donation Revenue', value: compactCurrency(donations.revenue || 0), current: donations.revenue || 0, prior: priorDonations.revenue || 0 },
      bottom: { label: 'Donations', value: (donations.count || 0).toLocaleString(), current: donations.count || 0, prior: priorDonations.count || 0 },
    },
    {
      top: { label: 'Membership Revenue', value: compactCurrency(memberships.revenue || 0), current: memberships.revenue || 0, prior: priorMemberships.revenue || 0 },
      bottom: { label: 'Active Memberships', value: (memberships.count || 0).toLocaleString(), current: memberships.count || 0, prior: priorMemberships.count || 0 },
    },
    {
      top: { label: 'Cash Sales', value: compactCurrency(cash.revenue || 0), current: cash.revenue || 0, prior: priorCash.revenue || 0 },
      bottom: { label: 'Cash Orders', value: (cash.count || 0).toLocaleString(), current: cash.count || 0, prior: priorCash.count || 0 },
    },
  ];

  const dateModeToggle = (
    <>
      <span style={{ fontSize: 13, color: 'var(--sl-text-tertiary)', fontWeight: 500, whiteSpace: 'nowrap' }}>View by:</span>
      <Tabs
        variant="button-border"
        size="sm"
        collapseBelow={0}
        items={DATE_MODE_TABS}
        value={dateMode}
        onChange={setDateMode}
      />
    </>
  );

  return (
    <ReportingPageLayout
      team={team}
      activeKey="sales"
      title="Sales"
      subtitle="Ticket sales, add-ons, memberships, donations, and cash sales across your venues."
      dateRange={dateRange}
      setDateRange={setDateRange}
      isMobile={isMobile}
      sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
      filterSidebarOpen={filterSidebarOpen}
      setFilterSidebarOpen={setFilterSidebarOpen}
      activeFilters={activeFilters}
      filterGroups={filterGroups}
      handleFilterChange={handleFilterChange}
      handleRemoveFilter={handleRemoveFilter}
      handleClearAllFilters={handleClearAllFilters}
      filterTitle="Filter Sales"
      filterSubtitle="Apply filters to narrow sales data."
      toolbarExtras={dateModeToggle}
    >
          {error && (
            <Alert color="error" role="alert" title={error} />
          )}

          {loading ? (
            <>
              <KpiCardGrid loading={true} isMobile={isMobile} columns={5} cards={[]} comparisonLabel={cmpLabel} />
              <SalesCardSkeleton />
              <SalesCardSkeleton />
              <SalesCardSkeleton />
              <SalesCardSkeleton />
              <SalesCardSkeleton />
              <SalesCardSkeleton />
            </>
          ) : !error && (
            <>
              <KpiCardGrid
                loading={false}
                isMobile={isMobile}
                columns={5}
                cards={kpiCards}
                comparisonLabel={cmpLabel}
              />

              <SalesCard
                title="Tickets"
                titleLink={`/teams/${team.id}/reporting/dashboard/sales-tickets`}
                metricValue={currencyFormatter.format(tickets.revenue || 0)}
                metricLabel={`${(tickets.count || 0).toLocaleString()} tickets sold`}
                chartData={tickets.over_time || []}
                chartColor="#1982C4"
                chartType={chartType}
                isCurrency={true}
                countLabel="tickets"
                seriesData={venueSeries ? venueSeries.tickets : null}
                seriesNames={venueNames}
                priorChartData={priorSeries(priorTickets)}
                dateRange={dateRange}
              />

              <SalesCard
                title="Add-Ons"
                titleLink={`/teams/${team.id}/reporting/dashboard/sales-addons`}
                metricValue={currencyFormatter.format(addOns.revenue || 0)}
                metricLabel={`${(addOns.count || 0).toLocaleString()} add-ons sold`}
                chartData={addOns.over_time || []}
                chartColor="#8AC926"
                chartType={chartType}
                isCurrency={true}
                countLabel="add-ons"
                seriesData={venueSeries ? venueSeries.add_ons : null}
                seriesNames={venueNames}
                priorChartData={priorSeries(priorAddOns)}
                dateRange={dateRange}
              />

              <SalesCard
                title="Donations"
                titleLink={`/teams/${team.id}/reporting/dashboard/sales-donations`}
                metricValue={currencyFormatter.format(donations.revenue || 0)}
                metricLabel={`${(donations.count || 0).toLocaleString()} donations`}
                chartData={donations.over_time || []}
                chartColor="#FAB714"
                chartType={chartType}
                isCurrency={true}
                countLabel="donations"
                seriesData={venueSeries ? venueSeries.donations : null}
                seriesNames={venueNames}
                priorChartData={priorSeries(priorDonations)}
                dateRange={dateRange}
              />

              <SalesCard
                title="Memberships"
                titleLink={`/teams/${team.id}/reporting/dashboard/sales/memberships`}
                metricValue={currencyFormatter.format(memberships.revenue || 0)}
                metricLabel={`${(memberships.count || 0).toLocaleString()} memberships`}
                chartData={memberships.over_time || []}
                chartColor="#6A4C93"
                chartType={chartType}
                isCurrency={false}
                countLabel="memberships"
                priorChartData={priorSeries(priorMemberships)}
                dateRange={dateRange}
              />

              <SalesCard
                title="Packs & Passes"
                titleLink={`/teams/${team.id}/reporting/dashboard/sales/packs`}
                metricValue={currencyFormatter.format(packs.revenue || 0)}
                metricLabel={`${(packs.count || 0).toLocaleString()} packs & passes sold`}
                chartData={packs.over_time || []}
                chartColor="#E86A92"
                chartType={chartType}
                isCurrency={true}
                countLabel="packs & passes"
                priorChartData={priorSeries(priorPacks)}
                dateRange={dateRange}
              />

              <SalesCard
                title="Cash Sales"
                metricValue={currencyFormatter.format(cash.revenue || 0)}
                metricLabel={`${(cash.count || 0).toLocaleString()} cash orders`}
                description="Total cash collected from orders paid in cash (cash tendered minus change given). Includes tickets, add-ons, donations, fees and taxes on those orders."
                chartData={cash.over_time || []}
                chartColor="#2A9D8F"
                chartType={chartType}
                isCurrency={true}
                countLabel="cash orders"
                seriesData={venueSeries ? venueSeries.cash : null}
                seriesNames={venueNames}
                priorChartData={priorSeries(priorCash)}
                dateRange={dateRange}
              />
            </>
          )}
    </ReportingPageLayout>
  );
}

export default ReportingSalesPage;
